// apps/nos/client/src/components/Workbench/IngredientSearch/IngredientSearch.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { logEvent } from "@/lib/eventLogger";
import {
  useIngredientFamilies,
  useIngredientRoleCounts,
  useIngredientSearch,
} from "@/hooks/useIngredientSearch";
import type { FormulationEntry } from "@/utils/types/compound";
import type { IngredientFilters } from "@/utils/types/ingredient";
import { EMPTY_INGREDIENT_FILTERS } from "@/utils/types/ingredient";
import { VertexRecommendations } from "@/components/Workbench/RightRail/VertexRecommendations";
import type { VertexRecommendationRow } from "@/services/vertexRecommendations_api";
import type { ActiveRadarSelection } from "@/components/Workbench/RightRail/SensoryAnalysisRail";
import { IngredientSearchHead } from "./IngredientSearchHead";
import { IngredientSearchRow } from "./IngredientSearchRow";

type IngredientSearchProps = {
  flavorId: string | null;
  formulaId?: number | null;
  inFlask: FormulationEntry[];
  onAdd: (id: string) => void;
  /** DEMO (impl-demo-mock) — routes a recommendation-row click (with the full
   *  row) so the page can mock the viz/loss move from the original ingredient.
   *  Falls back to onAdd when absent. Remove with services/demo. */
  onApplyRecommendation?: (row: VertexRecommendationRow) => void;
  versionId?: number | null;
  /** Review-stage preview cache key. When set, the ranked list + vertex
   *  cards recompute against the unsaved proposal instead of versionId. */
  previewId?: string | null;
  /** When true, the panel renders but blocks all interaction. Used while
   *  Auto-Formulate is mid-cycle (the user can't add ingredients while
   *  N-Block is staging changes). Filters, scrolling, and the +Add row
   *  buttons are all disabled; the panel itself dims via `aria-disabled`
   *  / pointer-events: none in CSS. */
  disabled?: boolean;
  /** Active vertex selection from the Sensory Radar. When non-null, a
   *  pinned "Recommended for {vertex}" section renders at the top of the
   *  search results body, listing the top-5 compounds that orthogonally
   *  improve the selected vertex (or its receptor's underlying dims). */
  radarSelection?: ActiveRadarSelection | null;
  /** DEMO (impl-demo-mock) — "compound:<id>" set that stays addable even when
   *  already in the flask (pinned demo rows). Remove with services/demo. */
  forceAddable?: ReadonlySet<string>;
  /** DEMO (impl-demo-mock) — "compound:<id>" set to hide from the list entirely
   *  (a pinned demo compound that's been added at its conc default, so it
   *  disappears on refetch rather than lingering). Remove with services/demo. */
  hidden?: ReadonlySet<string>;
};

export function IngredientSearch({
  flavorId,
  formulaId = null,
  inFlask,
  onAdd,
  onApplyRecommendation,
  versionId = null,
  previewId = null,
  disabled = false,
  radarSelection = null,
  forceAddable,
  hidden,
}: IngredientSearchProps) {
  const [filters, setFilters] = useState<IngredientFilters>(EMPTY_INGREDIENT_FILTERS);

  const search = useIngredientSearch(flavorId, filters, versionId, previewId);
  const families = useIngredientFamilies(flavorId);
  const roleCounts = useIngredientRoleCounts(flavorId, filters, versionId);

  const items = useMemo(() => {
    const all = search.data?.pages.flatMap((p) => p.items) ?? [];
    // DEMO (impl-demo-mock) — drop compounds the demo marks as consumed (added
    // at their conc default) so they disappear from the list on refetch.
    return hidden && hidden.size > 0
      ? all.filter((r) => !hidden.has(r.compoundId))
      : all;
  }, [search.data, hidden]);

  const inSet = useMemo(
    () => new Set(inFlask.map((f) => f.compoundId)),
    [inFlask],
  );

  // Virtualization — DOM bounded; React Query cache holds all pages.
  // measureElement gives the virtualizer the real rendered height so rows
  // don't overlap when content (two-line text, family stripe, etc.) takes
  // more vertical space than the initial estimate.
  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowCount = items.length + (search.hasNextPage ? 1 : 0);
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 46,
    overscan: 6,
    measureElement: (el) => el.getBoundingClientRect().height,
  });

  // Fetch next page when the sentinel (extra row at the end) is visible.
  const virtualItems = rowVirtualizer.getVirtualItems();
  useEffect(() => {
    const last = virtualItems.at(-1);
    if (!last) return;
    if (
      last.index >= items.length - 1 &&
      search.hasNextPage &&
      !search.isFetchingNextPage
    ) {
      void search.fetchNextPage();
    }
  }, [virtualItems, items.length, search.hasNextPage, search.isFetchingNextPage, search.fetchNextPage]);

  return (
    <div
      className={`panel is-panel${disabled ? " is-panel-disabled" : ""}`}
      aria-disabled={disabled || undefined}
      {...(disabled
        ? { style: { pointerEvents: "none" as const, opacity: 0.55 } }
        : {})}
    >
      <IngredientSearchHead
        filters={filters}
        setFilters={setFilters}
        flavorId={flavorId}
        families={families.data?.families ?? []}
        roleCounts={
          roleCounts.data ?? {
            total: items.length,
            counts: {},
            roleAllCount: items.length,
            kindCounts: { compound: 0, food: 0 },
            familyCounts: {},
          }
        }
      />

      <div
        ref={parentRef}
        className="is-body"
        style={{ overflow: "auto", flex: 1, minHeight: 0 }}
      >
        {radarSelection && (
          <VertexRecommendations
            flavorId={flavorId}
            formulaId={formulaId}
            versionId={versionId}
            previewId={previewId}
            selection={radarSelection}
            hasFormulation={inFlask.length > 0}
            onAdd={onAdd}
            onApplyRecommendation={onApplyRecommendation}
            inFlask={inSet}
          />
        )}
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: "relative",
            width: "100%",
          }}
        >
          {virtualItems.map((vi) => {
            // Wrap each row in a positioned container that the virtualizer
            // can measure via `data-index` + ref. The inner row/button
            // keeps its native height; the wrapper just absolutely
            // positions it inside the scroll body.
            const wrapperStyle: React.CSSProperties = {
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${vi.start}px)`,
            };

            if (vi.index >= items.length) {
              return (
                <div
                  key="is-sentinel"
                  data-index={vi.index}
                  ref={rowVirtualizer.measureElement}
                  style={wrapperStyle}
                >
                  <div
                    className="is-result-row"
                    style={{
                      justifyContent: "center",
                      color: "var(--ink-4)",
                      fontSize: 11,
                    }}
                  >
                    Loading…
                  </div>
                </div>
              );
            }
            const row = items[vi.index]!;
            return (
              <div
                key={row.compoundId}
                data-index={vi.index}
                ref={rowVirtualizer.measureElement}
                style={wrapperStyle}
              >
                <IngredientSearchRow
                  row={row}
                  // DEMO (impl-demo-mock) — force-addable demo rows stay
                  // clickable even when already in the flask.
                  isIn={inSet.has(row.compoundId) && !forceAddable?.has(row.compoundId)}
                  onAdd={() => {
                    logEvent("flavor_workbench_formulation_altered", {
                      action: "added",
                      flavor_id: flavorId,
                      formula_id: formulaId,
                      compound_id: row.compoundId,
                      amount_value: null,
                      amount_unit: null,
                    });
                    onAdd(row.compoundId);
                  }}
                  style={{}}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
